import PropTypes from 'prop-types'
import {Box, useTheme} from '@mui/material'
import Plot from 'react-plotly.js'
import {CSVLink} from 'react-csv'
import BouncingLoader from '../BouncingLoader'
import {
  StyledMessageContainer,
  StyledUserMessageBox,
  StyledBotMessageBox,
  StyledChartContainer,
  StyledSourceCodeBox,
  StyledToolTip,
  StyledActionButtonsContainer,
  StyledActionButtonsLeft,
  StyledCopyActionButton,
  StyledDownloadActionButton,
  PlotContainer,
  SmallDownloadIcon,
  SmallSourceCodeIcon,
  SmallCopyIcon,
  BoldNameText,
  BoldNameTextWithSpacing,
  HiddenCSVLink,
  getPlotlyTitleConfig,
  calculatePlotlyMargin
} from '../ChatbotStyles'
import {handleTextFormatting, getFormattedTimestamp} from '../helpers'
import {hasDownloadableData, wrapTitle} from '../utils/chatUtils'

const MessageList = ({
  prismProgram,
  messages,
  isDragging,
  isRequestActive,
  isInitialized,
  getUserAdditionalProps,
  handlePromptClick,
  handleUserMessageKeyDown,
  getBotAdditionalProps,
  canExport,
  handleChartDownloadMenuOpen,
  handleToggle,
  csvFileRef,
  canCopy,
  handleCopy,
  handleDownloadMenuOpen,
  getAIDisclaimerText
}) => {
  const theme = useTheme()

  if (!messages) {
    return null
  }

  const lastMessage = messages.at(-1)
  const hasRecentUserMessage = lastMessage && lastMessage.type === 'User' && !lastMessage.isPrompt
  const hasStreamingBotMessage = messages.some((msg) => msg.type === 'Bot' && msg.isStreaming)

  // Show loader when there's an active request and no streaming response,
  // or when session is initializing (not initialized but has active request)
  const isWaitingForResponse = hasRecentUserMessage && !hasStreamingBotMessage && isRequestActive
  const isInitializingSession = !isInitialized && isRequestActive && !hasStreamingBotMessage
  const showPendingLoader = isWaitingForResponse || isInitializingSession

  return (
    <>
      {messages
        .filter((msgObj) => !msgObj.wasCancelled)
        .map((msgObj) => {
          return msgObj.type === 'User' ? (
            <StyledMessageContainer key={msgObj.id} disableSelection={isDragging}>
              <BoldNameText variant='subtitle2' messageType={msgObj.type}>
                {msgObj.label}
              </BoldNameText>
              <StyledUserMessageBox
                isPrompt={msgObj.isPrompt}
                {...getUserAdditionalProps(msgObj)}
                onClick={() => handlePromptClick(msgObj)}
                onKeyDown={(e) => handleUserMessageKeyDown(e, msgObj)}
                tabIndex={0}
              >
                {msgObj.message}
              </StyledUserMessageBox>
            </StyledMessageContainer>
          ) : (
            <StyledMessageContainer key={msgObj.id} disableSelection={isDragging}>
              <BoldNameTextWithSpacing variant='subtitle2' messageType={msgObj.type}>
                {msgObj.label}
              </BoldNameTextWithSpacing>
              <StyledBotMessageBox tabIndex={0} {...getBotAdditionalProps(msgObj)}>
                {msgObj.isChart && msgObj.plotlyJson ? (
                  <Box>
                    {(() => {
                      try {
                        const plotlyConfig =
                          typeof msgObj.plotlyJson === 'string' ? JSON.parse(msgObj.plotlyJson) : msgObj.plotlyJson

                        // Calculate title configuration using styles
                        const titleText = plotlyConfig.layout?.title?.text || plotlyConfig.layout?.title || ''
                        const wrappedTitle = wrapTitle(titleText)
                        const titleLines = wrappedTitle ? wrappedTitle.split('<br>').length : 1

                        const sanitizedLayout = {
                          ...plotlyConfig.layout,
                          autosize: false,
                          height: 400,
                          dragmode: false,
                          title: '',
                          annotations: [
                            ...(plotlyConfig.layout?.annotations || []),
                            ...(wrappedTitle ? [getPlotlyTitleConfig(wrappedTitle, theme)] : [])
                          ],
                          margin: calculatePlotlyMargin(titleLines, plotlyConfig.layout?.margin)
                        }

                        return (
                          <StyledChartContainer>
                            <PlotContainer>
                              <Plot
                                divId={`sse-chart-${msgObj.id}`}
                                data={plotlyConfig.data}
                                layout={sanitizedLayout}
                                config={{
                                  displaylogo: false,
                                  displayModeBar: false,
                                  responsive: false,
                                  staticPlot: false
                                }}
                                style={{
                                  width: '100%',
                                  height: '400px'
                                }}
                                useResizeHandler={false}
                              />
                            </PlotContainer>
                            <StyledActionButtonsContainer>
                              <StyledActionButtonsLeft>
                                {canExport && (
                                  <StyledToolTip title='Download'>
                                    <StyledDownloadActionButton
                                      onClick={(event) => handleChartDownloadMenuOpen(event, msgObj)}
                                      data-testid='download-sse-chart-menu'
                                    >
                                      <SmallDownloadIcon />
                                    </StyledDownloadActionButton>
                                  </StyledToolTip>
                                )}
                                <StyledToolTip title={msgObj.showSourceCode ? 'Hide source code' : 'Show source code'}>
                                  <StyledCopyActionButton
                                    data-testid='CodeIcon'
                                    onClick={() => handleToggle(msgObj, 'SourceCode')}
                                  >
                                    <SmallSourceCodeIcon />
                                  </StyledCopyActionButton>
                                </StyledToolTip>
                              </StyledActionButtonsLeft>
                            </StyledActionButtonsContainer>
                          </StyledChartContainer>
                        )
                      } catch (error) {
                        return <div>Error rendering chart: {error.message}</div>
                      }
                    })()}
                    {msgObj.showSourceCode && (
                      <Box>
                        <StyledSourceCodeBox tabIndex={0}>
                          {handleTextFormatting(msgObj.generatedPlotlyCode || 'No source code available')}
                        </StyledSourceCodeBox>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <>
                    {handleTextFormatting(msgObj.responseText)}
                    {msgObj.isStreaming && <BouncingLoader inline={true} />}

                    {msgObj.graphData != null && (
                      <Box>
                        <HiddenCSVLink
                          component={CSVLink}
                          ref={csvFileRef}
                          data={msgObj.csvData || []}
                          headers={msgObj.csvHeaders || []}
                          filename={`data_${getFormattedTimestamp()}.csv`}
                        />
                        <StyledChartContainer>
                          <Plot
                            divId={'chart-' + msgObj.id}
                            config={msgObj.graphData.config}
                            data={msgObj.graphData.data}
                            layout={{
                              ...msgObj.graphData.layout,
                              autosize: true,
                              height: msgObj.graphData.layout?.height || 400 // Use provided height or default to 400px
                            }}
                          />
                        </StyledChartContainer>

                        {msgObj.showSourceCode && (
                          <Box>
                            <StyledSourceCodeBox tabIndex={0}>
                              {handleTextFormatting(msgObj.sourceCode)}
                            </StyledSourceCodeBox>
                          </Box>
                        )}
                      </Box>
                    )}
                  </>
                )}

                {!msgObj.wasCancelled &&
                  msgObj.responseText &&
                  !msgObj.isStreaming &&
                  msgObj.responseText !== 'Message was interrupted during page refresh.' &&
                  Object.keys(msgObj).includes('feedback') && (
                    <StyledActionButtonsContainer>
                      <StyledActionButtonsLeft>
                        {canCopy && (
                          <StyledToolTip title='Copy response'>
                            <StyledCopyActionButton onClick={() => handleCopy(msgObj.responseText)}>
                              <SmallCopyIcon />
                            </StyledCopyActionButton>
                          </StyledToolTip>
                        )}

                        {hasDownloadableData(msgObj) && canExport && (
                          <StyledToolTip title='Download'>
                            <StyledDownloadActionButton onClick={(event) => handleDownloadMenuOpen(event, msgObj)}>
                              <SmallDownloadIcon />
                            </StyledDownloadActionButton>
                          </StyledToolTip>
                        )}
                      </StyledActionButtonsLeft>
                    </StyledActionButtonsContainer>
                  )}

                {!msgObj.wasCancelled &&
                  !msgObj.isStreaming &&
                  msgObj.responseText !== 'Message was interrupted during page refresh.' &&
                  getAIDisclaimerText(msgObj)}
              </StyledBotMessageBox>
              <Box id={'msg-end-' + msgObj.id} />
            </StyledMessageContainer>
          )
        })}
      {showPendingLoader && (
        <StyledMessageContainer disableSelection={isDragging}>
          <BoldNameTextWithSpacing variant='subtitle2' messageType='Bot'>
            {prismProgram} Agent
          </BoldNameTextWithSpacing>
          <StyledBotMessageBox>
            <BouncingLoader inline={true} />
          </StyledBotMessageBox>
        </StyledMessageContainer>
      )}
      <Box id={'conversation-end'} />
    </>
  )
}

MessageList.propTypes = {
  prismProgram: PropTypes.string.isRequired,
  messages: PropTypes.array,
  isDragging: PropTypes.bool.isRequired,
  isRequestActive: PropTypes.bool.isRequired,
  isInitialized: PropTypes.bool.isRequired,
  getUserAdditionalProps: PropTypes.func.isRequired,
  handlePromptClick: PropTypes.func.isRequired,
  handleUserMessageKeyDown: PropTypes.func.isRequired,
  getBotAdditionalProps: PropTypes.func.isRequired,
  canExport: PropTypes.bool.isRequired,
  handleChartDownloadMenuOpen: PropTypes.func.isRequired,
  handleToggle: PropTypes.func.isRequired,
  csvFileRef: PropTypes.object.isRequired,
  canCopy: PropTypes.bool.isRequired,
  handleCopy: PropTypes.func.isRequired,
  handleDownloadMenuOpen: PropTypes.func.isRequired,
  getAIDisclaimerText: PropTypes.func.isRequired
}

export default MessageList

/*
MESSAGE LIST COMPONENT

KEY FUNCTIONS:
- handlePromptClick: Processes guiding prompt selections
- handleToggle: Shows/hides source code sections
- handleCopy: Copies message content to clipboard
- handleChartDownloadMenuOpen/handleDownloadMenuOpen: Opens export menus
- getBotAdditionalProps/getUserAdditionalProps: Provides accessibility attributes

MESSAGE TYPES:
- User messages: Display user input with prompt interaction support
- Bot messages: Support text, charts (Plotly), and data tables
- Streaming messages: Show bouncing loader during response generation
- Chart messages: Interactive Plotly charts with download options

FEATURES:
- Real-time streaming with bouncing loader
- Expandable sections (explanation, sources, SQL queries)
- Copy/download functionality with permission checks
- Feedback system with like/dislike options
- Accessibility support with ARIA labels
- Error handling for chart rendering
*/
